import { createRoot } from "react-dom/client";

// After a deploy, Vite rotates the content-hashed chunk filenames. A browser tab still
// running the previous build requests an old chunk that no longer exists; the dynamic
// import() then fails with "Failed to fetch dynamically imported module". Reload once to
// pick up the fresh index.html and chunk map. The sessionStorage guard prevents a reload
// loop when a chunk is genuinely broken rather than just stale.
/**
 * Reloads the page once to recover from a stale content-hashed chunk after a
 * deploy, using a sessionStorage timestamp to avoid reload loops.
 */
function reloadForStaleChunk(): void {
  const KEY = "staleChunkReloadedAt";
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last < 10_000) return;
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
}

// Lazy route chunks (React Router `lazy: () => import(...)`) load through Vite's preload
// helper, which dispatches this event when a chunk fails to load.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  reloadForStaleChunk();
});

const container = document.getElementById("root");

/**
 * Mounts the app into #root once window._APP_CONFIG is populated.
 */
function mountApp(): void {
  if (container == null) return;
  const root = createRoot(container);
  import("./app")
    .then(({ App }) => {
      root.render(<App />);
    })
    .catch(reloadForStaleChunk);
}

// env.json is the primary config source; env.js (window._APP_CONFIG, set by the
// <script src="/env.js"> tag in index.html) is the fallback, used only when
// env.json is missing or unparseable.
fetch(`${location.origin}/env.json`)
  .then((response) => response.json())
  .then((json) => {
    window._APP_CONFIG = json;
    mountApp();
  })
  .catch(() => {
    // env.json unavailable — fall back to env.js (window._APP_CONFIG).
    if (window._APP_CONFIG) {
      mountApp();
    }
  });
